import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth } from '@/lib/firebase'
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore'

// Mesma variavel usada no resto do app (.env.local em dev, .env.production.local
// em produção). Ja vem com /api no final, entao as rotas abaixo nao repetem.
const API_BASE_URL = import.meta.env.VITE_API_URL

interface Variation {
  id: number
  headline: string
  subheadline: string
  body: string
  cta: string
  angle: string
}

export default function CampaignNewPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'briefing' | 'variations'>('briefing')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [variations, setVariations] = useState<Variation[]>([])
  const [selected, setSelected] = useState<number | null>(null)

  const [form, setForm] = useState({
    productName: '',
    targetAudience: '',
    objective: '',
    segment: '',
    whatsapp: '',
    whatsappWelcomeMessage: '',
  })

  async function handleGenerate() {
    if (!form.productName || !form.targetAudience || !form.objective || !form.whatsapp) {
      setError('Preencha todos os campos obrigatórios.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const token = await auth.currentUser?.getIdToken()
      const res = await fetch(`${API_BASE_URL}/campaigns/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Erro ao gerar')
      }
      const data = await res.json()
      setVariations(data.variations)
      setStep('variations')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleUseVariation() {
    if (!selected) return
    const variation = variations.find(v => v.id === selected)
    if (!variation) return

    setSaving(true)
    setError('')
    try {
      const db = getFirestore()
      const uid = auth.currentUser?.uid || 'dev-user'

      // whatsappWelcomeMessage sai do form e vai pro Firestore como campo
      // separado (nao dentro de briefing) — o worker le ele direto assim.
      const { whatsappWelcomeMessage, ...briefingFields } = form

      const docRef = await addDoc(collection(db, 'campaigns'), {
        uid,
        status: 'draft',
        briefing: briefingFields,
        whatsappWelcomeMessage: whatsappWelcomeMessage || '',
        selectedVariation: variation,
        allVariations: variations,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      navigate(`/campanhas/${docRef.id}`)
    } catch (err: any) {
      setError('Erro ao salvar campanha: ' + err.message)
      setSaving(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/campanhas')} className="text-gray-400 hover:text-white transition-colors">
          ← Voltar
        </button>
        <h1 className="text-2xl font-bold text-white">Nova Campanha</h1>
      </div>

      {step === 'briefing' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Briefing da Campanha</h2>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 mb-4 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Produto ou serviço *</label>
                <input
                  type="text"
                  value={form.productName}
                  onChange={e => setForm({...form, productName: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="Ex: App de finanças pessoais"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Público-alvo *</label>
                <input
                  type="text"
                  value={form.targetAudience}
                  onChange={e => setForm({...form, targetAudience: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="Ex: Pessoas endividadas 25-45 anos"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Objetivo *</label>
                <select
                  value={form.objective}
                  onChange={e => setForm({...form, objective: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 transition-colors"
                >
                  <option value="" className="bg-gray-900">Selecione...</option>
                  <option value="gerar leads" className="bg-gray-900">Gerar leads</option>
                  <option value="vender produto" className="bg-gray-900">Vender produto</option>
                  <option value="aumentar seguidores" className="bg-gray-900">Aumentar seguidores</option>
                  <option value="promover evento" className="bg-gray-900">Promover evento</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Segmento</label>
                <input
                  type="text"
                  value={form.segment}
                  onChange={e => setForm({...form, segment: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="Ex: Finanças, Saúde, Educação"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">WhatsApp para receber os leads *</label>
                <input
                  type="text"
                  value={form.whatsapp}
                  onChange={e => setForm({...form, whatsapp: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="Ex: (51) 98921-1379"
                />
                <p className="text-xs text-gray-500 mt-1">
                  É pra esse número que a pessoa será direcionada no WhatsApp depois de enviar o formulário da landing page.
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Mensagem automática de boas-vindas</label>
                <textarea
                  value={form.whatsappWelcomeMessage}
                  onChange={e => setForm({...form, whatsappWelcomeMessage: e.target.value})}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                  placeholder="Olá {nome}! Recebemos seu contato e em breve vamos te dar mais detalhes."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enviada automaticamente pelo WhatsApp quando um lead se cadastra. Use <code className="text-gray-400">{'{nome}'}</code> pra inserir o nome do lead. Deixe em branco pra usar a mensagem padrão.
                </p>
              </div>
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-white transition-all disabled:opacity-50 mt-2"
                style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}
              >
                {loading ? 'Gerando com IA...' : '✨ Gerar variações com IA'}
              </button>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Como funciona</h2>
            <div className="space-y-4">
              {[
                { n: '1', t: 'Preencha o briefing', d: 'Descreva seu produto, público, objetivo e o WhatsApp da campanha' },
                { n: '2', t: 'IA gera 3 variações', d: 'O Gemini cria 3 versões de copy com ângulos diferentes' },
                { n: '3', t: 'Escolha a melhor', d: 'Selecione a variação que mais combina com sua marca' },
                { n: '4', t: 'Landing page pronta', d: 'Publicamos automaticamente sua página de captura, já com redirecionamento pro seu WhatsApp' },
              ].map(s => (
                <div key={s.n} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                    style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}>
                    {s.n}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{s.t}</p>
                    <p className="text-gray-400 text-sm">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 'variations' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Escolha a melhor variação</h2>
            <button onClick={() => setStep('briefing')} className="text-sm text-gray-400 hover:text-white">
              ← Refazer briefing
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 mb-4 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 mb-6">
            {variations.map(v => (
              <div
                key={v.id}
                onClick={() => setSelected(v.id)}
                className={`border rounded-2xl p-6 cursor-pointer transition-all ${
                  selected === v.id
                    ? 'border-indigo-500 bg-indigo-500/10'
                    : 'border-white/10 bg-white/5 hover:border-white/30'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/10 text-gray-300">
                    {v.angle}
                  </span>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selected === v.id ? 'border-indigo-500 bg-indigo-500' : 'border-white/30'
                  }`}>
                    {selected === v.id && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                </div>
                <h3 className="text-white font-bold text-lg mb-1">{v.headline}</h3>
                <p className="text-gray-300 text-sm mb-2">{v.subheadline}</p>
                <p className="text-gray-400 text-sm mb-3">{v.body}</p>
                <span className="inline-block px-4 py-2 rounded-lg text-sm font-medium text-white"
                  style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}>
                  {v.cta}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={handleUseVariation}
            disabled={!selected || saving}
            className="w-full py-3 rounded-xl font-semibold text-white transition-all disabled:opacity-40"
            style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}
          >
            {saving ? 'Salvando...' : 'Usar esta variação →'}
          </button>
        </div>
      )}
    </div>
  )
}